import React, { useEffect, useMemo, useState } from "react";
import { getReviews, createReview } from "../services/reviewServices";
import "./Reviews.css";

/* Simple star component */
const Star = ({ filled = false, size = 20 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    role="img"
    aria-label={filled ? "star filled" : "star"}
    className={`rev-star ${filled ? "rev-star--filled" : ""}`}
  >
    <path d="M12 .587l3.668 7.431 8.2 1.193-5.934 5.785 1.4 8.164L12 18.896l-7.334 3.864 1.4-8.164L.132 9.211l8.2-1.193L12 .587z" />
  </svg>
);

/* Row showing 5→1 rating distribution */
const RatingBar = ({ label, pct }) => (
  <div className="d-flex align-items-center gap-2 mb-1">
    <small className="text-muted" style={{ width: 24 }}>
      {label}
    </small>
    <div className="flex-grow-1 progress rev-progress">
      <div
        className="progress-bar"
        role="progressbar"
        style={{ width: `${pct}%` }}
        aria-valuenow={pct}
        aria-valuemin="0"
        aria-valuemax="100"
      />
    </div>
    <small className="text-muted" style={{ width: 38 }}>
      {pct}%
    </small>
  </div>
);

const ReviewsSection = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [msg, setMsg] = useState({ ok: true, text: "" });
  const [form, setForm] = useState({
    display_name: "",
    rating: 5,
    message: "",
  });
  const [hoverStars, setHoverStars] = useState(0);

  /* fetch reviews */
  useEffect(() => {
    (async () => {
      try {
        const data = await getReviews();
        setReviews(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error(e);
        setMsg({ ok: false, text: "Failed to load reviews." });
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  /* computed summary */
  const summary = useMemo(() => {
    if (!reviews.length) return { avg: 0, count: 0, dist: [0, 0, 0, 0, 0] };
    const count = reviews.length;
    const sum = reviews.reduce((a, r) => a + Number(r.rating || 0), 0);
    const avg = Math.round((sum / count) * 10) / 10;
    const dist = [0, 0, 0, 0, 0]; // indexes 0..4 -> stars 5..1
    reviews.forEach((r) => {
      const i = 5 - Math.min(5, Math.max(1, Number(r.rating)));
      dist[i] += 1;
    });
    const pct = dist.map((n) => Math.round((n / count) * 100));
    return { avg, count, pct };
  }, [reviews]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMsg({ ok: true, text: "" });

    const name = form.display_name.trim();
    const text = form.message.trim();
    const r = Number(form.rating);

    if (!name || !text)
      return setMsg({ ok: false, text: "Please fill in all fields." });
    if (!Number.isInteger(r) || r < 1 || r > 5)
      return setMsg({ ok: false, text: "Rating must be 1–5." });

    try {
      setSubmitting(true);
      const res = await createReview({
        display_name: name,
        rating: r,
        message: text,
      });
      // optimistic add to list (appears at top)
      if (res?.review) setReviews((prev) => [res.review, ...prev]);
      setForm({ display_name: "", rating: 5, message: "" });
      setHoverStars(0);
      setMsg({ ok: true, text: "Thank you! Your review has been submitted." });
    } catch (err) {
      console.error(err);
      setMsg({ ok: false, text: "Submission failed. Please try again." });
    } finally {
      setSubmitting(false);
      setTimeout(() => setMsg({ ok: true, text: "" }), 3500);
    }
  };

  const visualRating = hoverStars || form.rating;

  return (
    <section className="reviews-wrap">
      <div className="container">
        <h2 className="rev-title">Customer Reviews</h2>

        {/* Header summary */}
        <div className="rev-summary shadow-sm">
          <div className="rev-summary__left">
            <div className="rev-score">{summary.avg.toFixed(1)}</div>
            <div className="rev-stars">
              {[1, 2, 3, 4, 5].map((i) => (
                <Star key={i} filled={i <= Math.round(summary.avg)} />
              ))}
            </div>
            <div className="text-muted small">
              {summary.count} review{summary.count === 1 ? "" : "s"}
            </div>
          </div>
          <div className="rev-summary__right">
            {[5, 4, 3, 2, 1].map((s, idx) => (
              <RatingBar
                key={s}
                label={`${s}★`}
                pct={summary.pct?.[idx] ?? 0}
              />
            ))}
          </div>
        </div>

        {/* Form + List */}
        <div className="row g-4">
          <div className="col-lg-5">
            <div className="card rev-card">
              <div className="card-body">
                <h5 className="mb-3">Leave a Review</h5>

                {msg.text && (
                  <div
                    className={`alert ${
                      msg.ok ? "alert-success" : "alert-danger"
                    } py-2`}
                  >
                    {msg.text}
                  </div>
                )}

                <form onSubmit={handleSubmit}>
                  <div className="mb-3">
                    <label className="form-label">Name</label>
                    <input
                      type="text"
                      className="form-control"
                      value={form.display_name}
                      onChange={(e) =>
                        setForm({ ...form, display_name: e.target.value })
                      }
                      disabled={submitting}
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Rating</label>
                    <div className="rev-stars-input">
                      {[1, 2, 3, 4, 5].map((i) => (
                        <button
                          key={i}
                          type="button"
                          className="rev-star-btn"
                          onMouseEnter={() => setHoverStars(i)}
                          onMouseLeave={() => setHoverStars(0)}
                          onClick={() => setForm({ ...form, rating: i })}
                          aria-label={`${i} star`}
                        >
                          <Star filled={i <= visualRating} size={24} />
                        </button>
                      ))}
                      <span className="ms-2 small text-muted">
                        {visualRating}/5
                      </span>
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Message</label>
                    <textarea
                      className="form-control"
                      rows="3"
                      value={form.message}
                      onChange={(e) =>
                        setForm({ ...form, message: e.target.value })
                      }
                      disabled={submitting}
                    />
                  </div>

                  <button
                    className="btn btn-primary w-100 rev-submit"
                    disabled={submitting}
                  >
                    {submitting ? "Submitting..." : "Submit Review"}
                  </button>
                </form>
              </div>
            </div>
          </div>

          <div className="col-lg-7">
            {loading ? (
              <div className="rev-skeleton card shadow-sm mb-3" />
            ) : reviews.length === 0 ? (
              <p className="text-muted mt-2">No reviews yet.</p>
            ) : (
              <div className="rev-grid">
                {reviews.map((rev) => (
                  <article key={rev.id} className="card shadow-sm rev-item">
                    <div className="card-body">
                      <div className="d-flex align-items-center justify-content-between mb-1">
                        <h6 className="mb-0">{rev.display_name}</h6>
                        <small className="text-muted">
                          {new Date(rev.created_at).toLocaleDateString()}
                        </small>
                      </div>
                      <div className="mb-2">
                        {[1, 2, 3, 4, 5].map((i) => (
                          <Star key={i} size={18} filled={i <= rev.rating} />
                        ))}
                      </div>
                      <p className="mb-0">{rev.message}</p>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ReviewsSection;
